var _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

/*
This is essentially a more advanced `textarea`, using Draft.js
https://facebook.github.io/draft-js/

The important Draft.js concepts needed to understand this file are:
    - Everything is immutable, and inputs all result in a new `editorState`
      object being passed to `handleChange`.  All changes must be done by
      constructing new objects.  This means simply editing text involves
      creating a new ContentState, which is used to create a new EditorState
    - `EditorState` contains a `ContentState` property which contains the data
      relevant to the text content
    - `ContentState` is organized into individual "Blocks", which helps with
      performance as updates only affect a single block
    - Specific text in blocks can be denoted as "Entities", which can store
      data relevant to its text.  This is what allows backspacing a widget
      to result in its deletion in Perseus
    - Special styling is done using Decorators, which allow substituting text
      content with a custom react element
    - Modifier is a collection of helpful utilities for modification purposes

TODO(samiskin): Make tasks such as "addWidget" and "updateWidget" not functions
                that you call on the PerseusEditor component (Can do once this
                fully replacess the old editor).
*/

var React = require("react");

var _require = require("draft-js"),
    CharacterMetadata = _require.CharacterMetadata,
    Entity = _require.Entity,
    Editor = _require.Editor,
    EditorState = _require.EditorState,
    CompositeDecorator = _require.CompositeDecorator,
    ContentState = _require.ContentState,
    Modifier = _require.Modifier,
    genKey = _require.genKey,
    getDefaultKeyBinding = _require.getDefaultKeyBinding,
    KeyBindingUtil = _require.KeyBindingUtil;

var Widgets = require("./widgets.js");
var DraftUtils = require("./draft-utils.js");

// This controls the minimum time between when updates for the parent
// component are generated.  The best time for this number sort of depends
// on the user's typing speed though, as if the time between each letter being
// typed is longer than the throttle, they would notice a freeze when the update
// is being calculated.
// TODO(samiskin): Figure out whats the best value for this number
// 100 is best for my typing speed, but may not work as well for slower typists
var UPDATE_PARENT_THROTTLE = 100;

var widgetPlaceholder = "[[\u2603 {id}]]";
var widgetRegExp = /\[\[\u2603 [a-z-]+ [0-9]+\]\]/g;
var widgetPartsRegExp = /^\[\[\u2603 (([a-z-]+) ([0-9]+))\]\]$/;
var widgetRegexForId = function widgetRegexForId(id) {
    return new RegExp("(\\[\\[\u2603 " + id + "\\]\\])", "gm");
};
var partialWidgetRegex = /\[\[([a-z-]+)$/; // Used for autocompletion

var imageRegExp = /!\[[^\]]*?\]\([^\)].*?\)/g;

// Note: Nested decorators currently do not work, therefore this will not
//       work when nesting bold/italics/underline.  Hopefully this is
//       fixed in future versions of Draft.js
var boldRegExp = /\*\*([\s\S]+?)\*\*(?!\*)/g;
var italicsRegExp = /\**(?:^|[^*])((\*|_)(\w+(\s\w+)*)\2)/g; // copied from https://github.com/ayberkt/RFMarkdownTextView/blob/387312e602f03b87f3ef82dc82c62df455d6fd30/RFMarkdownTextView/RFMarkdownSyntaxStorage.m
var boldItalicsRegExp = /(\*\*\*\w+(\s\w+)*\*\*\*)/g;
var underlineRegExp = /__([\s\S]+?)__(?!_)/g;
var headerRegExp = /^ *(#{1,6})([^\n]+)$/g;

/*
    Styled ranges in Draft.js are done using a `CompositeDecorator`,
    where a `strategy` is given to denote what ranges of text to style,
    and a `component` is given to denote how that range should be rendered
*/
var entityStrategy = function entityStrategy(contentBlock, callback, type) {
    return contentBlock.findEntityRanges(function (char) {
        return char.getEntity() && Entity.get(char.getEntity()).type === type;
    }, callback);
};

var styledBlock = function styledBlock(props, style) {
    return React.createElement(
        "span",
        _extends({}, props, { style: style }),
        props.children
    );
};
styledBlock.propTypes = { children: React.PropTypes.any };

var highlightedBlock = function highlightedBlock(props, backgroundColor) {
    return styledBlock(props, { backgroundColor: backgroundColor });
};

var entityColorDecorator = function entityColorDecorator(type, color) {
    return {
        strategy: function strategy() {
            for (var _len = arguments.length, args = Array(_len), _key = 0; _key < _len; _key++) {
                args[_key] = arguments[_key];
            }

            return entityStrategy.apply(undefined, args.concat([type]));
        },
        component: function component(props) {
            return highlightedBlock(props, color);
        }
    };
};

var regexColorDecorator = function regexColorDecorator(regex, color) {
    return {
        strategy: function strategy() {
            for (var _len2 = arguments.length, args = Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
                args[_key2] = arguments[_key2];
            }

            return DraftUtils.regexStrategy.apply(DraftUtils, args.concat([regex]));
        },
        component: function component(props) {
            return highlightedBlock(props, color);
        }
    };
};

var boldDecorator = {
    strategy: function strategy() {
        for (var _len3 = arguments.length, args = Array(_len3), _key3 = 0; _key3 < _len3; _key3++) {
            args[_key3] = arguments[_key3];
        }

        return DraftUtils.regexStrategy.apply(DraftUtils, args.concat([boldRegExp]));
    },
    component: function component(props) {
        return styledBlock(props, { fontWeight: "bold" });
    }
};

// The italics regex has a group that ensures that the *___* block
// does not include the * used to create a list.  Since this results
// in match.index also including the first non-capturing group, we must
// use custom logic for this strategy
var italicsStrategy = function italicsStrategy() {
    for (var _len4 = arguments.length, args = Array(_len4), _key4 = 0; _key4 < _len4; _key4++) {
        args[_key4] = arguments[_key4];
    }

    return DraftUtils.regexStrategy.apply(DraftUtils, args.concat([italicsRegExp, function (matchArr) {
        var start = matchArr.index + matchArr[0].length - matchArr[1].length;
        var end = start + matchArr[1].length;
        return { start: start, end: end };
    }]));
};
var italicsDecorator = {
    strategy: italicsStrategy,
    component: function component(props) {
        return styledBlock(props, { fontStyle: "italic" });
    }
};

var underlineDecorator = {
    strategy: function strategy() {
        for (var _len5 = arguments.length, args = Array(_len5), _key5 = 0; _key5 < _len5; _key5++) {
            args[_key5] = arguments[_key5];
        }

        return DraftUtils.regexStrategy.apply(DraftUtils, args.concat([underlineRegExp]));
    },
    component: function component(props) {
        return styledBlock(props, { textDecoration: "underline" });
    }
};

var boldItalicsDecorator = {
    strategy: function strategy() {
        for (var _len6 = arguments.length, args = Array(_len6), _key6 = 0; _key6 < _len6; _key6++) {
            args[_key6] = arguments[_key6];
        }

        return DraftUtils.regexStrategy.apply(DraftUtils, args.concat([boldItalicsRegExp]));
    },
    component: function component(props) {
        return styledBlock(props, {
            fontWeight: "bold",
            fontStyle: "italic"
        });
    }
};

// TODO: Make the headers also able to scale with the rest of the text
// when changing the fontSize percentage
var headerComponent = function headerComponent(props) {
    var text = props.decoratedText;
    var headerSize = text.split(headerRegExp)[1].length;
    var style = { marginBottom: 0 };
    return React.createElement("h" + headerSize, { style: style }, props.children);
};
headerComponent.propTypes = {
    decoratedText: React.PropTypes.string,
    children: React.PropTypes.any
};

var headerDecorator = {
    strategy: function strategy() {
        for (var _len7 = arguments.length, args = Array(_len7), _key7 = 0; _key7 < _len7; _key7++) {
            args[_key7] = arguments[_key7];
        }

        return DraftUtils.regexStrategy.apply(DraftUtils, args.concat([headerRegExp]));
    },
    component: headerComponent
};

var decorator = new CompositeDecorator([entityColorDecorator("WIDGET", "#DFD"), entityColorDecorator("TEMP_IMAGE", "#fdffdd"), regexColorDecorator(imageRegExp, "#dffdfa"), boldItalicsDecorator, boldDecorator, underlineDecorator, italicsDecorator, headerDecorator]);

// Key bindings are handled by mapping events to strings
var keyBindings = function keyBindings(e) {
    var isCommandPressed = KeyBindingUtil.hasCommandModifier(e);
    if (isCommandPressed && e.keyCode === 66) {
        // 66 = b
        return "perseus-bold";
    } else if (isCommandPressed && e.keyCode === 73) {
        // 73 = i
        return "perseus-italics";
    } else if (isCommandPressed && e.keyCode === 85) {
        // 85 = u
        return "perseus-underline";
    } else if (isCommandPressed && e.keyCode === 219) {
        // 219 = [
        return "perseus-decrease-font-size";
    } else if (isCommandPressed && e.keyCode === 221) {
        // 221 = ]
        return "perseus-increase-font-size";
    } else if (isCommandPressed && e.keyCode === 220) {
        // 220 = ]
        return "perseus-reset-font-size";
    } else {
        return getDefaultKeyBinding(e);
    }
};

/*
    This is the main Draft.js editor.  It keeps track of its internal Draft.js
    state, however what it exposes through its `onChange` is a simple string
    as well as a list of the currently active widgets.
*/
var PerseusEditor = React.createClass({
    displayName: "PerseusEditor",

    propTypes: {
        onChange: React.PropTypes.func,
        content: React.PropTypes.string,
        initialWidgets: React.PropTypes.any,
        placeholder: React.PropTypes.string,
        imageUploader: React.PropTypes.func,
        widgetEnabled: React.PropTypes.bool
    },

    getDefaultProps: function getDefaultProps() {
        return {
            onChange: function onChange() {},
            content: "",
            initialWidgets: {},
            widgetEnabled: true,
            placeholder: "Type here"
        };
    },

    getInitialState: function getInitialState() {
        var _props = this.props,
            content = _props.content,
            initialWidgets = _props.initialWidgets,
            widgetEnabled = _props.widgetEnabled;

        var contentState = ContentState.createFromText(content);
        var editorState = EditorState.createWithContent(contentState, decorator);

        if (widgetEnabled) {
            editorState = this._insertWidgetsAsEntities(editorState, initialWidgets);
        }

        return {
            editorState: editorState,
            widgets: initialWidgets,
            fontSizePercentage: 100
        };
    },


    // The editor can have its content changed completely by changing the
    // content prop, however if the data this component sent to its parent
    // using `this.props.onChange()` is being fed back in, ignore it
    componentDidUpdate: function componentDidUpdate(prevProps) {
        if (this.props.content !== this.lastContentUpdate) {
            this.lastContentUpdate = this.props.content;
            this.setState(this.getInitialState()); //eslint-disable-line
        }
    },


    // By turning widgets into Entities, we allow for widgets to be considered
    // "IMMUTABLE", that is, backspacing a widget will delete the entire text
    // rather than just a "]" character.  It also enables us to detect which
    // widget id has been deleted, as metadata can be attached to entities
    // TODO(samiskin): Turn this task of `applyEntities(pattern, createEntity)`
    // into a DraftUtils function
    _insertWidgetsAsEntities: function _insertWidgetsAsEntities(editorState, widgets) {
        var content = editorState.getCurrentContent();

        Object.keys(widgets).forEach(function (id) {
            var selection = DraftUtils.findPattern(content, widgetRegexForId(id)); //eslint-disable-line max-len
            if (selection) {
                // Sometimes the widgets don't actually exist
                var entity = Entity.create("WIDGET", "IMMUTABLE", { id: id });
                content = Modifier.applyEntity(content, selection, entity);
            }
        });

        // EditorState.set is used rather than push, because no state should
        // be added to the undo stack.
        var withEntity = EditorState.set(editorState, {
            currentContent: content
        });

        return withEntity;
    },
    _getDraftData: function _getDraftData() {
        var editorState = this.state.editorState;
        var contentState = editorState.getCurrentContent();
        var selection = editorState.getSelection();
        return { editorState: editorState, contentState: contentState, selection: selection };
    },
    _getNextWidgetId: function _getNextWidgetId(type) {
        var currWidgets = this.state.widgets;
        return Object.keys(currWidgets).filter(function (id) {
            return currWidgets[id].type === type;
        }).map(function (id) {
            return +id.split(" ")[1];
        }) //ids are (([a-z-]+) ([0-9]+))
        .reduce(function (maxId, currId) {
            return Math.max(maxId, currId);
        }, 0);
    },
    _createInitialWidget: function _createInitialWidget(widgetType) {
        // Since widgets are given IDs, adding a new widget must ensure that a
        // unique id is generated for it.
        var widgetNum = this._getNextWidgetId(widgetType);
        var id = widgetType + " " + (widgetNum + 1);
        var widget = {
            options: Widgets.getEditor(widgetType).defaultProps,
            type: widgetType,
            // Track widget version on creation, so that a widget editor
            // without a valid version prop can only possibly refer to a
            // pre-versioning creation time.
            version: Widgets.getVersion(widgetType)
        };
        return [id, widget];
    },
    addWidget: function addWidget(type) {
        var _this = this;

        this.focus(function () {
            return _this._handleChange(_this._insertNewWidget(type));
        });
    },
    _insertNewWidget: function _insertNewWidget(type, draftDataParams) {
        var _extends2;

        var draftData = _extends({}, this._getDraftData(), draftDataParams);

        var _createInitialWidget2 = this._createInitialWidget(type),
            id = _createInitialWidget2[0],
            widget = _createInitialWidget2[1];

        var newWidgets = _extends({}, this.state.widgets, (_extends2 = {}, _extends2[id] = widget, _extends2));
        var newDraftData = this._insertWidgetText(draftData, id);

        return {
            editorState: newDraftData.editorState,
            widgets: newWidgets
        };
    },
    _insertWidgetText: function _insertWidgetText(draftData, id) {
        // Text for the widget is inserted, and an entity is assigned
        var text = widgetPlaceholder.replace("{id}", id);
        var entity = Entity.create("WIDGET", "IMMUTABLE", { id: id });

        return DraftUtils.replaceSelection(draftData, text, entity);
    },
    updateWidget: function updateWidget(id, newProps) {
        var _extends3;

        this.setState({ widgets: _extends({}, this.state.widgets, (_extends3 = {}, _extends3[id] = newProps, _extends3)) });
    },


    // This function only removes the widget from the content, and then
    // handleChange handles removing widgets from the state, as widgets
    // can also be deleted by editor actions such as backspace and delete
    removeWidget: function removeWidget(id) {
        var _this2 = this;

        this.focus(function () {
            var _getDraftData2 = _this2._getDraftData(),
                editorState = _getDraftData2.editorState,
                contentState = _getDraftData2.contentState;

            var selection = DraftUtils.findPattern(contentState, widgetRegexForId(id)); //eslint-disable-line max-len
            var newDraftData = DraftUtils.deleteSelection({
                editorState: editorState,
                selection: selection
            });

            _this2._handleChange({ editorState: newDraftData.editorState });
        });
    },
    addTemplate: function addTemplate(templateType) {
        var _this3 = this;

        this.focus(function () {
            _this3._addTemplate(templateType);
        });
    },
    _addTemplate: function _addTemplate(templateType) {
        var _this4 = this;

        var _getDraftData3 = this._getDraftData(),
            editorState = _getDraftData3.editorState,
            contentState = _getDraftData3.contentState,
            selection = _getDraftData3.selection;

        var widgets = _extends({}, this.state.widgets);

        // Templates shouldn't interrupt a line if the cursor is not at the end
        var currBlock = contentState.getBlockForKey(selection.getEndKey());
        selection = DraftUtils.selectEnd(currBlock);

        // Insert a new line at the beginning if there is content there, that
        // way the template appears on a newline in the rendered markdown
        if (currBlock.getText().length > 0) {
            contentState = Modifier.splitBlock(contentState, selection);
            selection = contentState.getSelectionAfter();
        }

        if (templateType === "allWidgets") {
            var allTypes = Widgets.getAllWidgetTypes().sort();

            // Insert a newline at the beginning
            contentState = Modifier.splitBlock(contentState, selection);
            contentState = allTypes.reduce(function (content, type) {
                var _createInitialWidget3 = _this4._createInitialWidget(type),
                    id = _createInitialWidget3[0],
                    widget = _createInitialWidget3[1];

                widgets[id] = widget;
                content = _this4._insertWidgetText({
                    contentState: content,
                    selection: content.getSelectionAfter()
                }, id).contentState;
                return Modifier.splitBlock(
                // Put each widget on a new line
                content, content.getSelectionAfter());
            }, contentState);

            editorState = EditorState.push(editorState, contentState, "insert-fragment");
        } else {
            var template = "";
            if (templateType === "table") {
                template = "header 1 | header 2 | header 3\n" + "- | - | -\n" + "data 1 | data 2 | data 3\n" + "data 4 | data 5 | data 6\n" + "data 7 | data 8 | data 9";
            } else if (templateType === "titledTable") {
                template = "|| **Table title** ||\n" + "header 1 | header 2 | header 3\n" + "- | - | -\n" + "data 1 | data 2 | data 3\n" + "data 4 | data 5 | data 6\n" + "data 7 | data 8 | data 9";
            } else if (templateType === "alignment") {
                template = "$\\begin{align} x+5 &= 30 \\\\\n" + "x+5-5 &= 30-5 \\\\\n" + "x &= 25 \\end{align}$";
            } else if (templateType === "piecewise") {
                template = "$f(x) = \\begin{cases}\n" + "7 & \\text{if }x=1 \\\\\n" + "f(x-1)+5 & \\text{if }x > 1\n" + "\\end{cases}$";
            }

            editorState = DraftUtils.insertText({ editorState: editorState, contentState: contentState, selection: selection }, "\n" + template + "\n").editorState;
        }
        this._handleChange({ editorState: editorState, widgets: widgets });
    },
    _handleCopy: function _handleCopy() {
        var _this5 = this;

        var _getDraftData4 = this._getDraftData(),
            contentState = _getDraftData4.contentState,
            selection = _getDraftData4.selection;

        var entities = DraftUtils.getEntities(contentState, selection);

        var copiedWidgets = entities.reduce(function (map, entity) {
            var id = entity.getData().id;
            map[id] = _this5.state.widgets[id];
            return map;
        }, {});

        localStorage.perseusLastCopiedWidgets = JSON.stringify(copiedWidgets);
        return false;
    },


    // Widgets cannot have ID conflicts, therefore this function exists
    // to return a mapping of { new id -> safe id }
    _createSafeWidgetMapping: function _createSafeWidgetMapping(newWidgets, currentWidgets) {
        // Create a mapping of { type -> largest id of that type }
        var maxIds = Object.keys(currentWidgets).reduce(function (idMap, widget) {
            var _widget$split = widget.split(" "),
                type = _widget$split[0],
                id = _widget$split[1];

            idMap[type] = idMap[type] ? Math.max(idMap[type], +id) : +id;
            return idMap;
        }, {});

        var safeWidgetMapping = Object.keys(newWidgets).reduce(function (safeMap, widget) {
            var type = widget.split(" ")[0];
            maxIds[type] = maxIds[type] ? maxIds[type] + 1 : 1;
            safeMap[widget] = type + " " + maxIds[type];
            return safeMap;
        }, {});

        return safeWidgetMapping;
    },


    // Pasting text from another Perseus editor instance should also copy over
    // the widgets.  To do this properly, we must parse the text, replace the
    // widget ids with non-conflicting ones, store them, and also assign them
    // proper entities.  Sadly Draft.js only supports `handlePastedText` which
    // happens prior to the new content state being generated (which is needed
    // to add entities to).  We therefore must reimplement the default Paste
    // functionality, in order to add our custom steps afterwards
    _handlePaste: function _handlePaste(pastedText, html, selection) {
        // If no widgets are in localstorage, just use default behavior
        var sourceWidgetsJSON = localStorage.perseusLastCopiedWidgets;
        if (!sourceWidgetsJSON) {
            return false;
        }

        var sourceWidgets = JSON.parse(sourceWidgetsJSON);
        var widgets = _extends({}, this.state.widgets);
        var safeWidgetMapping = this._createSafeWidgetMapping(sourceWidgets, widgets); //eslint-disable-line max-len
        var charData = CharacterMetadata.create();

        // insertText takes a sanitizer function which gets ran on every
        // line.  It is used here in order to fix the new widgets to not
        // have conflicting IDs, as well as fill in the widget data
        var sanitizeText = function sanitizeText(textLine) {
            var sanitized = textLine.replace(new RegExp("\r", "g"), ""); //eslint-disable-line no-control-regex
            var characterList = Array(sanitized.length).fill(charData);
            var safeText = sanitized.replace(widgetRegExp, function (syntax, offset) {
                //eslint-disable-line max-len
                var match = widgetPartsRegExp.exec(syntax);
                var fullText = match[0]; // The entire [[ widgetName id ]]
                var widgetId = match[1]; // Just the "widgetName id" part
                var newId = safeWidgetMapping[widgetId];
                var newText = widgetPlaceholder.replace("{id}", newId);

                // Create an entity for the new widget, and assign it to the
                // characters that match up to the new widget text (splice)
                var entity = Entity.create("WIDGET", "IMMUTABLE", {
                    id: newId
                }); //eslint-disable-line max-len
                var entityChar = CharacterMetadata.applyEntity(charData, entity); //eslint-disable-line max-len
                var entityChars = Array(newText.length).fill(entityChar);
                characterList.splice.apply(characterList, [offset, fullText.length].concat(entityChars));

                widgets[newId] = sourceWidgets[widgetId];
                return fullText.replace(widgetId, newId);
            });
            return { text: safeText, characterList: characterList };
        };

        var data = this._getDraftData();
        data.selection = selection || data.selection;

        var _DraftUtils$insertTex = DraftUtils.insertText(data, pastedText, sanitizeText),
            editorState = _DraftUtils$insertTex.editorState;

        this._handleChange({ editorState: editorState, widgets: widgets });
        return true; // True means draft doesn't run its default behavior
    },
    _handleDrop: function _handleDrop(selection, dataTransfer) {
        // All insertions are done to the end of the current block
        var contentState = this.state.editorState.getCurrentContent();
        var endKey = selection.getEndKey();
        var endBlock = contentState.getBlockForKey(endKey);
        var endSelection = DraftUtils.selectEnd(endBlock);

        var imageUrl = dataTransfer.getLink();
        if (imageUrl) {
            // Adds new lines and collapses the selection
            var _DraftUtils$insertTex2 = DraftUtils.insertText(_extends({}, this._getDraftData(), { selection: endSelection }), "\n![](" + imageUrl + ")"),
                editorState = _DraftUtils$insertTex2.editorState;

            this._handleChange({ editorState: editorState });
        } else {
            var text = dataTransfer.getText();
            return this._handlePaste(text, null, endSelection);
        }

        return true; // Disable default draft drop handler
    },
    _handleDroppedFiles: function _handleDroppedFiles(selection, files) {
        var _this6 = this;

        var images = files.filter(function (file) {
            return file.type.match("image.*");
        });
        var contentState = this.state.editorState.getCurrentContent();
        images.forEach(function (image) {
            // Insert placeholder text to show that the image is being uploaded
            var text = "![](" + image.name + "...)";
            var id = genKey();
            var entity = Entity.create("TEMP_IMAGE", "IMMUTABLE", { id: id });

            var charData = CharacterMetadata.create().merge({ entity: entity });
            var characterList = Array(text.length).fill(charData);
            var sanitizer = function sanitizer(textLine) {
                return textLine === text ? { text: text, characterList: characterList } : null;
            };

            var blockKey = selection.getEndKey();
            var contentBlock = contentState.getBlockForKey(blockKey);
            var endOfBlockSelection = DraftUtils.selectEnd(contentBlock);
            contentState = DraftUtils.insertText({ contentState: contentState, selection: endOfBlockSelection }, "\n" + text + "\n", sanitizer).contentState;

            // Begin uploading the image, and update the link once complete
            _this6.props.imageUploader(image, function (url) {
                var currEditor = _this6.state.editorState;
                var currContent = currEditor.getCurrentContent();
                var placeholderLocation = DraftUtils.findEntity(currContent, function (c) {
                    return c.getData().id === id;
                });
                var newDraftData = DraftUtils.replaceSelection({
                    editorState: currEditor,
                    contentState: currContent,
                    selection: placeholderLocation
                }, "![](" + url + ")");
                _this6._handleChange({ editorState: newDraftData.editorState });
            });
        });
        var editorState = EditorState.push(this.state.editorState, contentState, "insert-fragment");
        this._handleChange({ editorState: editorState });
        return true; // Disable default draft drop handler
    },


    // This implements tab completion for widgets.  When the user
    // has typed [[d, then presses tab, we should replace [[d
    // with the full [[ {emoji} dropdown 1 ]] text
    _handleTab: function _handleTab(e) {
        var _getDraftData5 = this._getDraftData(),
            contentState = _getDraftData5.contentState,
            selection = _getDraftData5.selection;

        // isCollapsed means that there is no active selection, its just
        // a blinking cursor.  For the SelectionState object, this
        // essentially means that anchorOffset === focusOffset


        if (!selection.isCollapsed() || !this.props.widgetEnabled) {
            return;
        }
        e.preventDefault();

        var currBlock = contentState.getBlockForKey(selection.getEndKey());
        var text = currBlock.getText().substring(0, selection.getEndOffset());
        var match = text.match(partialWidgetRegex);
        if (match) {
            var partialName = match[1];
            var allWidgets = Widgets.getAllWidgetTypes();
            var matchingWidgets = allWidgets.filter(function (widget) {
                return widget.substring(0, partialName.length) === partialName;
            });

            // If only one match is available, complete it
            if (matchingWidgets.length === 1) {
                var widgetType = matchingWidgets[0];
                var replacementArea = selection.merge({
                    anchorOffset: match.index
                });

                this._handleChange(this._insertNewWidget(widgetType, {
                    selection: replacementArea
                }));
            }
        }
        return true; // Say that we've handled the event, no other work needed
    },
    _getDecorationForStyle: function _getDecorationForStyle(style) {
        switch (style) {
            case "perseus-bold":
                return "**";
            case "perseus-italics":
                return "*";
            case "perseus-underline":
                return "__";
            default:
                return null;
        }
    },
    _handleKeyCommand: function _handleKeyCommand(command) {
        // Check if the font size should be changed
        var fontSizePercentage = this.state.fontSizePercentage;

        if (command === "perseus-increase-font-size") {
            this.setState({ fontSizePercentage: fontSizePercentage + 10 });
            return true;
        } else if (command === "perseus-decrease-font-size") {
            this.setState({ fontSizePercentage: fontSizePercentage - 10 });
            return true;
        } else if (command === "perseus-reset-font-size") {
            this.setState({ fontSizePercentage: 100 });
            return true;
        }

        // Check whether a style such as bold/italics/underline should be added
        var decoration = this._getDecorationForStyle(command);
        if (decoration !== null) {
            var data = this._getDraftData();

            var _DraftUtils$toggleDec = DraftUtils.toggleDecoration(data, decoration),
                editorState = _DraftUtils$toggleDec.editorState;

            this._handleChange({ editorState: editorState });
            return true;
        }

        return false;
    },


    lastContentUpdate: "",
    _updateParent: function _updateParent(content, widgets) {
        // The parent component should know of only the active widgets,
        // however the widgets are not deleted from this.state because a
        // user undoing a widget deletion should also recover the
        // widget's metadata
        var currEntities = DraftUtils.getEntities(content);
        var visibleWidgets = currEntities.reduce(function (map, entity) {
            var id = entity.getData().id;
            map[id] = widgets[id];
            return map;
        }, {});

        this.lastContentUpdate = content.getPlainText("\n");

        // Provide the parent component with the current text
        // representation, as well as the current active widgets
        this.props.onChange({
            content: this.lastContentUpdate,
            widgets: visibleWidgets
        });
    },


    pastContentState: null,
    lastIdleCallback: null,
    _handleChange: function _handleChange(newState) {
        var _this7 = this;

        var state = _extends({}, this.state, newState);
        var widgets = state.widgets;
        var editorState = state.editorState;

        // The cursor should not exist within an entity
        editorState = DraftUtils.snapSelectionOutsideEntities({ editorState: editorState }, this.state.editorState.getSelection()).editorState;

        var newContent = editorState.getCurrentContent();

        // editorState contains more than just the content, such as the current
        // cursor position.  This means `handleChange` gets called for more than
        // just content changes, so certain calculations aren't always needed.
        if (newContent !== this.pastContentState) {
            // This ensures that unless the content stops changing for a certain
            // short duration, no processing will be done to update the parent.
            // This allows the editing to remain performant for large files,
            // as basic tasks only occur on individual ContentBlocks, while
            // updating the parent involves iterating through them all
            clearTimeout(this.lastIdleCallback);
            this.lastIdleCallback = setTimeout(function () {
                return _this7._updateParent(newContent, widgets);
            }, UPDATE_PARENT_THROTTLE);
        }

        this.pastContentState = newContent;
        this.setState({ editorState: editorState, widgets: widgets });
    },


    // HACK: There are currently serious Draft.js bugs related to mutating the
    //       editorState when it is not in focus, then pressing undo.  This
    //       workaround uses a callback parameter to run code after the
    //       editorState has been updated to be in focus, that way functions
    //       such as addWidget will not bring up serious issues when undone
    focus: function focus(callback) {
        this.editor.focus();
        var editorState = this.state.editorState;
        editorState = EditorState.set(editorState, {
            selection: editorState.getSelection().set("hasFocus", true),
            forceSelection: true
        });
        this.setState({ editorState: editorState }, callback);
    },
    render: function render() {
        var _this8 = this;

        return React.createElement(
            "div",
            {
                onCopy: this._handleCopy,
                onCut: this._handleCopy,
                onDragStart: this._handleCopy,
                style: {
                    fontSize: this.state.fontSizePercentage + "%"
                }
            },
            React.createElement(Editor, {
                ref: function ref(e) {
                    return _this8.editor = e;
                },
                editorState: this.state.editorState,
                onChange: function onChange(editorState) {
                    return _this8._handleChange({ editorState: editorState });
                },
                spellCheck: true,
                stripPastedStyles: true,
                placeholder: this.props.placeholder,
                handlePastedText: this._handlePaste,
                handleDroppedFiles: this._handleDroppedFiles,
                handleDrop: this._handleDrop,
                keyBindingFn: keyBindings,
                handleKeyCommand: this._handleKeyCommand,
                onTab: this._handleTab
            })
        );
    }
});

module.exports = PerseusEditor;


/*****************
 ** WEBPACK FOOTER
 ** ./src/perseus-editor.jsx
 ** module id = 92
 ** module chunks = 0 2
 **/